import React, { useEffect, useState } from 'react';

type MarketingFocusKey = 'Awareness' | 'Reputation' | 'Offers';

export function MarketingView({ onNavigate, data, onOpenUpload }: { onNavigate: (v: any) => void; data?: any; onOpenUpload?: () => void }) {
    const fallbackData = {
        kpis: {
            reputationScore: 4.7,
            reputationMax: 5.0,
            reviewVelocity: 42,
            reviewVelocityDelta: 8,
            totalReach: 120000,
            totalReachDelta: 14.2,
            engagementRate: 6.1,
            engagementDelta: 0.8
        },
        alertSummary: {
            title: "Damage Control: Reputation Alert",
            subtitle: "2 Negative signals detected on Social Media",
            priority: "High Priority",
            insight: "A personalized response to the TikToker could convert the 12k reach into a PR win. Draft a response and recovery plan."
        },
        alerts: [
            { platform: "TikTok", user: "@foodie_hater", rating: 1, text: "Wait time was 45 mins after booking. Terrible management.", date: "4h ago", avatar: "FH", impact: "High Reach (12k views)" },
            { platform: "Google", user: "John D.", rating: 2, text: "Burger was cold. Service was slow today.", date: "6h ago", avatar: "JD", impact: "Profile Visibility" }
        ],
        contentFocus: {
            defaultFocus: "Awareness",
            copy: {
                Awareness: {
                    headline: "Boost weekend reach",
                    sub: "Short-form video + local hashtags around dinner rush.",
                    channel: "Instagram Reels",
                    window: "Fri 18:00–20:00",
                    metricLabel: "Reach Lift",
                    metricValue: "+18%",
                    cta: "Build 7-day plan"
                },
                Reputation: {
                    headline: "Reduce negative sentiment",
                    sub: "Prioritize replies to 1–2 star reviews within 2 hours.",
                    channel: "Google Reviews",
                    window: "Daily 09:00–11:00",
                    metricLabel: "Risk Level",
                    metricValue: "Moderate",
                    cta: "Open response queue"
                },
                Offers: {
                    headline: "Drive weekday traffic",
                    sub: "Limited lunch offer with upsell bundle to lift covers.",
                    channel: "Meta + In-store",
                    window: "Mon–Wed 11:00–13:00",
                    metricLabel: "Projected Uplift",
                    metricValue: "+12%",
                    cta: "Launch offer"
                }
            } as Record<MarketingFocusKey, any>
        },
        platforms: [
            { name: "Instagram", icon: "IG", status: "Connected", followers: "12.4k", engagement: "4.8%" },
            { name: "TikTok", icon: "TT", status: "Connected", followers: "8.2k", engagement: "12.1%" },
            { name: "Facebook", icon: "FB", status: "Connected", followers: "5.1k", engagement: "2.3%" },
            { name: "Google", icon: "GG", status: "Connected", rating: "4.7", reviews: "842" },
        ],
        reach: {
            series: [40, 55, 45, 70, 65, 80, 75, 90, 85, 100, 95, 110, 105, 120],
            labels: ["1 Jan", "15 Jan", "30 Jan"]
        },
        topPosts: [
            { id: 1, platform: "Instagram", type: "Reel", reach: "24.5k", engagement: "1.2k", sentiment: "Positive", date: "2d ago", thumbnail: "R" },
            { id: 2, platform: "TikTok", type: "Video", reach: "48.1k", engagement: "5.4k", sentiment: "Highly Positive", date: "1d ago", thumbnail: "V" },
            { id: 3, platform: "Instagram", type: "Photo", reach: "8.2k", engagement: "420", sentiment: "Neutral", date: "4d ago", thumbnail: "P" },
            { id: 4, platform: "Facebook", type: "Post", reach: "3.1k", engagement: "156", sentiment: "Positive", date: "1w ago", thumbnail: "P" },
        ],
        reviews: [
            { platform: "Google", user: "Alex M.", rating: 5, text: "The truffle fries are a must-try! Best service in the area.", date: "1d ago", avatar: "AM" },
            { platform: "TripAdvisor", user: "Sarah J.", rating: 4, text: "Great atmosphere, though it was quite busy on Friday night.", date: "3d ago", avatar: "SJ" },
            { platform: "Google", user: "Michael R.", rating: 5, text: "Excellent burgers and the accurate wait times were spot on.", date: "5d ago", avatar: "MR" }
        ]
    };

    const merged = data || fallbackData;
    const platforms = merged.platforms || fallbackData.platforms;
    const topPosts = merged.topPosts || fallbackData.topPosts;
    const alerts = merged.alerts || fallbackData.alerts;
    const alertSummary = merged.alertSummary || fallbackData.alertSummary;
    const focusCopy = merged.contentFocus?.copy || fallbackData.contentFocus.copy;
    const reachSeries = merged.reach?.series || fallbackData.reach.series;
    const reachLabels = merged.reach?.labels || fallbackData.reach.labels;
    const kpis = merged.kpis || fallbackData.kpis;
    const reviews = merged.reviews || fallbackData.reviews;

    const [showUploadMenu, setShowUploadMenu] = useState(false);

    const [contentFocus, setContentFocus] = useState<MarketingFocusKey>((merged.contentFocus?.defaultFocus as MarketingFocusKey) || 'Awareness');
    const [activeReview, setActiveReview] = useState<number | null>(0);

    useEffect(() => {
        const preferred = (merged.contentFocus?.defaultFocus as MarketingFocusKey) || 'Awareness';
        setContentFocus(preferred);
    }, [merged.contentFocus?.defaultFocus]);

    useEffect(() => {
        if (!reviews || reviews.length === 0) {
            setActiveReview(null);
            return;
        }
        if (activeReview === null || activeReview >= reviews.length) {
            setActiveReview(0);
        }
    }, [reviews, activeReview]);

    const activeFocus = focusCopy[contentFocus] || focusCopy.Awareness;

    const formatCompact = (value: number) => {
        if (!Number.isFinite(value)) return "0";
        if (value >= 1000000) return `${(value / 1000000).toFixed(value >= 10000000 ? 0 : 1)}m`;
        if (value >= 1000) return `${(value / 1000).toFixed(value >= 10000 ? 0 : 1)}k`;
        return value.toString();
    };

    const maxReach = Math.max(...reachSeries.map((v: any) => Number(v) || 0), 1);
    const reachHeight = (value: number) => {
        const normalized = Number(value) / maxReach;
        const scaled = Math.max(0.1, Math.min(1, normalized));
        const percent = Math.round((scaled * 88) + 8);
        return `${percent}%`;
    };

    return React.createElement("div", { className: "space-y-8 animate-in fade-in duration-500 pb-12" }, [
        // Page Title & Header
        React.createElement("div", {
            key: "header-main",
            className: "relative overflow-visible rounded-[32px] border border-indigo-100/60 bg-white p-8 shadow-sm",
            style: {
                background: "linear-gradient(135deg, rgba(99,102,241,0.10) 0%, rgba(255,255,255,0.98) 40%, rgba(14,165,233,0.08) 100%)"
            }
        }, [
            React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-600 via-sky-400 to-emerald-300" }),
            React.createElement("div", { className: "flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6" }, [
                React.createElement("div", {}, [
                    React.createElement("h1", { className: "text-3xl font-black text-gray-900 tracking-tight mb-2" }, "Marketing & Reputation"),
                    React.createElement("p", { className: "text-gray-500 font-medium max-w-2xl leading-relaxed" },
                        "Monitor your social reach, engage with customers, and manage your online reputation across all platforms from a unified command center."
                    )
                ]),
                React.createElement("div", { className: "relative" }, [
                    React.createElement("button", {
                        onClick: () => setShowUploadMenu(!showUploadMenu),
                        className: "group flex items-center gap-2 bg-indigo-600 text-white px-5 py-2 rounded-full text-[10px] font-black uppercase tracking-widest shadow-lg shadow-indigo-100/50 transition-all hover:scale-105 active:scale-95"
                    }, [
                        React.createElement("span", { key: "icon", className: "text-base group-hover:rotate-90 transition-transform duration-300" }, "+"),
                        "Upload"
                    ]),
                    showUploadMenu && React.createElement("div", {
                        className: "absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2"
                    }, [
                        React.createElement("button", {
                            className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2",
                            onClick: () => {
                                if (onOpenUpload) onOpenUpload();
                                else onNavigate("upload");
                                setShowUploadMenu(false);
                            }
                        }, "Upload Document"),
                        React.createElement("button", {
                            className: "w-full text-left px-4 py-3 hover:bg-gray-50 text-sm font-bold text-gray-700 flex items-center gap-2 border-t border-gray-50",
                            onClick: () => { onNavigate("manual-marketing"); setShowUploadMenu(false); }
                        }, "Data Form")
                    ])
                ])
            ])
        ]),

        // Executive Snapshot KPIs
        React.createElement("div", { key: "kpi-strip", className: "grid grid-cols-1 md:grid-cols-4 gap-4" }, [
            React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between min-h-[120px]" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-300 opacity-70" }),
                React.createElement("p", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, "Reputation Score"),
                React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-2" }, `${kpis.reputationScore.toFixed(1)} / ${kpis.reputationMax.toFixed(1)}`),
                React.createElement("p", { className: "text-[11px] text-slate-500 mt-1" }, "All platforms combined")
            ]),
            React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between min-h-[120px]" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-emerald-500 via-sky-400 to-indigo-300 opacity-70" }),
                React.createElement("p", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, "Review Velocity"),
                React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-2" }, `${Math.round(kpis.reviewVelocity)} / 30d`),
                React.createElement("p", { className: `text-[11px] mt-1 font-semibold ${kpis.reviewVelocityDelta >= 0 ? "text-emerald-600" : "text-rose-600"}` }, `${kpis.reviewVelocityDelta >= 0 ? "+" : ""}${kpis.reviewVelocityDelta}% vs last month`)
            ]),
            React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between min-h-[120px]" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500 via-violet-400 to-sky-300 opacity-70" }),
                React.createElement("p", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, "Total Reach (30d)"),
                React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-2" }, formatCompact(kpis.totalReach)),
                React.createElement("p", { className: `text-[11px] mt-1 font-semibold ${kpis.totalReachDelta >= 0 ? "text-emerald-600" : "text-rose-600"}` }, `${kpis.totalReachDelta >= 0 ? "+" : ""}${kpis.totalReachDelta}% growth`)
            ]),
            React.createElement("div", { className: "relative overflow-hidden bg-white p-4 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col justify-between min-h-[120px]" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-amber-400 via-rose-400 to-orange-300 opacity-70" }),
                React.createElement("p", { className: "text-[10px] font-black text-slate-400 uppercase tracking-widest" }, "Engagement Rate"),
                React.createElement("p", { className: "text-2xl font-black text-slate-900 mt-2" }, `${kpis.engagementRate}%`),
                React.createElement("p", { className: "text-[11px] text-slate-500 mt-1" }, `${kpis.engagementDelta >= 0 ? "+" : ""}${kpis.engagementDelta} pts vs avg`)
            ])
        ]),

        // Priority Alerts + Content Focus
        React.createElement("div", { key: "alerts-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" }, [
            React.createElement("div", { key: "damage-control", className: "relative overflow-hidden bg-white rounded-3xl border border-rose-200/70 shadow-sm max-h-[350px] flex flex-col" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-rose-500 via-amber-400 to-orange-300" }),
                React.createElement("div", { className: "p-4 border-b border-rose-100 flex justify-between items-center bg-rose-50/70" }, [
                    React.createElement("div", { className: "flex items-center gap-3" }, [
                        React.createElement("div", { className: "w-10 h-10 bg-rose-100 rounded-xl flex items-center justify-center text-[11px] font-black text-rose-700 shadow-sm border border-rose-200" }, "ALRT"),
                        React.createElement("div", {}, [
                            React.createElement("h3", { className: "font-black text-red-900" }, alertSummary.title),
                            React.createElement("p", { className: "text-xs text-rose-700 font-bold" }, alertSummary.subtitle)
                        ])
                    ]),
                    React.createElement("div", { className: "bg-rose-100 text-rose-700 px-3 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-tighter shadow-sm border border-rose-200" }, alertSummary.priority)
                ]),
                React.createElement("div", { className: "p-4 space-y-2" },
                    alerts.map((rev: any, i: number) =>
                        React.createElement("div", { key: i, className: "bg-white p-2.5 rounded-xl border border-rose-200/70 flex flex-col gap-1.5" }, [
                            React.createElement("div", { className: "flex items-center justify-between" }, [
                                React.createElement("div", { className: "flex items-center gap-3" }, [
                                    React.createElement("div", { className: "w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-[10px] font-black text-rose-700 shadow-inner border border-rose-200 flex-shrink-0" }, rev.avatar),
                                    React.createElement("div", {}, [
                                        React.createElement("p", { className: "text-[11px] font-black text-gray-900" }, rev.user),
                                        React.createElement("p", { className: "text-[9px] text-slate-400 font-bold uppercase tracking-wider" }, rev.platform)
                                    ])
                                ]),
                                React.createElement("div", { className: "text-rose-600 text-[9px] font-black" }, `${rev.rating}/5`)
                            ]),
                            React.createElement("p", { className: "text-[10px] text-slate-600 italic line-clamp-2" }, `"${rev.text}"`),
                            React.createElement("div", { className: "flex items-center justify-between text-[8px] font-bold uppercase tracking-wider text-slate-400" }, [
                                React.createElement("span", {}, rev.impact),
                                React.createElement("span", {}, rev.date)
                            ])
                        ])
                    )
                ),
                React.createElement("div", { className: "p-3 bg-rose-50/70 border-t border-rose-100 flex items-center gap-3" }, [
                    React.createElement("span", { className: "text-[9px] font-black text-rose-700 bg-white px-2 py-1 rounded-full border border-rose-200" }, "Insight"),
                    React.createElement("p", { className: "text-[10px] text-rose-800 font-medium" },
                        React.createElement("strong", {}, "Insight: "),
                        alertSummary.insight
                    )
                ])
            ]),
            React.createElement("div", { key: "content-focus", className: "relative overflow-hidden bg-white text-slate-900 rounded-3xl border border-slate-200/80 shadow-sm max-h-[350px] flex flex-col" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-300" }),
                React.createElement("div", { className: "p-4 flex flex-col" }, [
                    React.createElement("div", { className: "flex items-start justify-between gap-4" }, [
                        React.createElement("div", {}, [
                            React.createElement("p", { className: "text-[10px] font-black uppercase tracking-widest text-slate-500" }, "Content Focus"),
                            React.createElement("h3", { className: "text-[15px] font-black mt-2 text-slate-900" }, "Content & Campaign Focus"),
                            React.createElement("p", { className: "text-[11px] text-slate-500 mt-2" }, "Select a focus to preview recommended actions.")
                        ]),
                        React.createElement("div", { className: "inline-flex flex-col gap-1 p-1 bg-slate-100 border border-slate-200 rounded-xl" },
                            (["Awareness", "Reputation", "Offers"] as const).map(option =>
                                React.createElement("button", {
                                    key: option,
                                    onClick: () => setContentFocus(option),
                                    className: `px-2.5 py-1 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all ${contentFocus === option ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:bg-white'}`
                                }, option)
                            )
                        )
                    ]),
                    React.createElement("div", { className: "mt-3 bg-slate-50 border border-slate-200 rounded-2xl p-3 hover:bg-white transition-colors" }, [
                        React.createElement("p", { className: "text-[9px] font-black uppercase tracking-widest text-slate-500" }, "This Week"),
                        React.createElement("p", { className: "text-[13px] font-black mt-2 text-slate-900" }, activeFocus.headline),
                        React.createElement("p", { className: "text-[11px] text-slate-600 mt-1.5" }, activeFocus.sub)
                    ]),
                    React.createElement("div", { className: "mt-3 grid grid-cols-2 gap-3" }, [
                        React.createElement("div", { className: "rounded-2xl border border-slate-200 bg-slate-50 p-3 hover:bg-white transition-colors" }, [
                            React.createElement("p", { className: "text-[9px] uppercase tracking-widest text-slate-500 font-black" }, "Best Channel"),
                            React.createElement("p", { className: "text-[11px] font-black mt-1.5 text-slate-900" }, activeFocus.channel)
                        ]),
                        React.createElement("div", { className: "rounded-2xl border border-slate-200 bg-slate-50 p-3 hover:bg-white transition-colors" }, [
                            React.createElement("p", { className: "text-[9px] uppercase tracking-widest text-slate-500 font-black" }, "Best Window"),
                            React.createElement("p", { className: "text-[11px] font-black mt-1.5 text-slate-900" }, activeFocus.window)
                        ])
                    ]),
                    React.createElement("div", { className: "mt-3 flex items-center justify-between" }, [
                        React.createElement("div", {}, [
                            React.createElement("p", { className: "text-[9px] uppercase tracking-widest text-slate-500 font-black" }, activeFocus.metricLabel),
                            React.createElement("p", { className: "text-lg font-black mt-1 text-slate-900" }, activeFocus.metricValue)
                        ]),
                        React.createElement("button", { className: "px-3 py-1.5 rounded-xl bg-slate-900 text-white text-[9px] font-black uppercase tracking-widest hover:bg-slate-800 transition-all" }, activeFocus.cta)
                    ])
                ])
            ])
        ]),

        // 2. Platform Connection Summary
        React.createElement("div", { key: "platforms", className: "grid grid-cols-1 md:grid-cols-4 gap-4" },
            platforms.map((p: any) =>
                React.createElement("div", { key: p.name, className: "relative overflow-hidden bg-white p-5 rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-all group flex flex-col min-h-[140px]" }, [
                    React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-300 opacity-70" }),
                    React.createElement("div", { className: "flex justify-between items-start mb-4" }, [
                        React.createElement("span", { className: "text-[11px] font-black text-slate-600 bg-slate-100 px-2 py-1 rounded-lg border border-slate-200" }, p.icon),
                        React.createElement("span", { className: "text-[10px] bg-emerald-50 text-emerald-700 px-2 py-1 rounded-full font-black uppercase border border-emerald-200/70" }, p.status)
                    ]),
                    React.createElement("h3", { className: "font-black text-gray-900 mb-1" }, p.name),
                    React.createElement("div", { className: "flex items-baseline gap-2" }, [
                        React.createElement("p", { className: "text-xl font-black text-indigo-600" }, p.followers || p.rating),
                        React.createElement("p", { className: "text-xs text-slate-400 font-bold" }, p.followers ? "Followers" : "Rating")
                    ])
                ])
            )
        ),

        // 3. Main Analytics Row
        React.createElement("div", { key: "analytics-row", className: "grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch" }, [
            // Reach Trend Chart Placeholder
            React.createElement("div", { className: "relative overflow-hidden bg-white p-6 rounded-2xl border border-slate-200/80 shadow-sm flex flex-col max-h-[350px]" }, [
                React.createElement("div", { className: "absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-indigo-500 via-sky-400 to-emerald-300 opacity-60" }),
                React.createElement("div", { className: "flex justify-between items-center mb-8" }, [
                    React.createElement("div", {}, [
                        React.createElement("h3", { className: "font-black text-gray-900" }, "Total Social Reach"),
                        React.createElement("p", { className: "text-xs text-slate-400 font-bold" }, "Aggregated reach across all platforms (Last 30 days)")
                    ]),
                    React.createElement("div", { className: `flex items-center gap-2 px-3 py-1.5 rounded-xl border ${kpis.totalReachDelta >= 0 ? "bg-emerald-50 text-emerald-700 border-emerald-200/70" : "bg-rose-50 text-rose-700 border-rose-200/70"}` }, [
                        React.createElement("span", { className: "text-xs font-black" }, `${kpis.totalReachDelta >= 0 ? "+" : ""}${kpis.totalReachDelta}%`),
                        React.createElement("span", { className: "text-[10px] font-black uppercase tracking-widest" }, kpis.totalReachDelta >= 0 ? "Up" : "Down")
                    ])
                ]),
                // Simulated Chart
                React.createElement("div", { className: "flex-1 flex items-end justify-between gap-1 px-2" },
                    reachSeries.map((h: any, i: number) =>
                        React.createElement("div", {
                            key: i,
                            className: "flex-1 bg-gradient-to-t from-indigo-500/90 to-indigo-400/80 rounded-t-sm opacity-80 hover:opacity-100 transition-all cursor-pointer relative group hover:shadow-md",
                            style: { height: reachHeight(Number(h) || 0) }
                        }, [
                            React.createElement("div", { className: "absolute bottom-full mb-2 left-1/2 -translate-x-1/2 bg-gray-900 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10" },
                                `Day ${i + 1}: ${formatCompact(Number(h) || 0)}`
                            )
                        ])
                    )
                ),
                React.createElement("div", { className: "flex justify-between mt-3 px-2" },
                    reachLabels.map((d: string) => React.createElement("span", { key: d, className: "text-[10px] font-bold text-slate-400 uppercase" }, d))
                )
            ]),

            // Top Content (Executive-Friendly)
            React.createElement("div", { key: "content-cards", className: "bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden flex flex-col max-h-[350px]" }, [
                React.createElement("div", { className: "p-6 border-b border-slate-100 flex justify-between items-center" }, [
                    React.createElement("h3", { className: "font-black text-gray-900" }, "Top Content This Month"),
                    React.createElement("button", { className: "text-xs font-bold text-indigo-600 hover:underline" }, "View Full Report →")
                ]),
                React.createElement("div", { className: "p-4 grid grid-cols-1 gap-3 flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-transparent scrollbar-track-transparent focus:scrollbar-thumb-slate-200 focus:scrollbar-track-transparent" },
                    topPosts.slice(0, 3).map((post: any, idx: number) =>
                        React.createElement("div", { key: post.id ?? `${post.platform || 'post'}-${idx}`, className: "bg-slate-50/70 border border-slate-200 rounded-lg p-2.5 flex flex-col gap-1.5 hover:bg-white transition-colors cursor-pointer" }, [
                            React.createElement("div", { className: "flex items-center gap-2" }, [
                                React.createElement("div", { className: "w-8 h-8 bg-white rounded-lg flex items-center justify-center text-[10px] font-black text-slate-600 border border-slate-200 shadow-sm" }, post.thumbnail),
                                React.createElement("div", { className: "min-w-0" }, [
                                    React.createElement("p", { className: "text-xs font-black text-slate-900 truncate" }, post.type),
                                    React.createElement("p", { className: "text-[9px] text-slate-400 font-bold uppercase tracking-wider truncate" }, `${post.platform} • ${post.date}`)
                                ])
                            ]),
                            React.createElement("div", { className: "flex items-center justify-between text-[10px]" }, [
                                React.createElement("span", { className: "text-slate-500 font-semibold" }, "Reach"),
                                React.createElement("span", { className: "font-black text-slate-900" }, post.reach)
                            ]),
                            React.createElement("div", { className: "flex items-center justify-between text-[10px]" }, [
                                React.createElement("span", { className: "text-slate-500 font-semibold" }, "Engagement"),
                                React.createElement("span", { className: "font-black text-indigo-600" }, post.engagement)
                            ]),
                            React.createElement("span", {
                                className: `inline-flex w-fit px-2 py-0.5 rounded-full text-[8px] font-black uppercase ${post.sentiment.includes("Highly") ? "bg-emerald-100 text-emerald-700" :
                                    post.sentiment.includes("Positive") ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-600"
                                    }`
                            }, post.sentiment)
                        ])
                    )
                )
            ])
        ]),

        // 5. Customer Reviews Section (Standard)
        React.createElement("div", { key: "reviews", className: "bg-white rounded-2xl border border-slate-200/80 shadow-sm overflow-hidden max-h-[350px] flex flex-col w-full lg:w-[calc(50%-12px)]" }, [
            React.createElement("div", { className: "p-5 border-b border-slate-100 flex justify-between items-center" }, [
                React.createElement("div", {}, [
                    React.createElement("h3", { className: "font-black text-gray-900" }, "Recent Reviews"),
                    React.createElement("p", { className: "text-xs text-slate-400 font-bold" }, "Latest feedback across Google, TripAdvisor, and Yelp")
                ]),
                React.createElement("div", { className: "flex gap-2" }, [
                    React.createElement("div", { className: "bg-amber-50 text-amber-700 px-3 py-1 rounded-xl text-xs font-black flex items-center gap-1.5 border border-amber-200/70" }, [
                        `Rating ${kpis.reputationScore.toFixed(1)}`, React.createElement("span", { className: "opacity-50" }, `/ ${kpis.reputationMax.toFixed(1)}`)
                    ]),
                    React.createElement("div", { className: "bg-indigo-50 text-indigo-700 px-3 py-1 rounded-xl text-xs font-black" },
                        kpis.reviewCount ? `${kpis.reviewCount} Reviews` : `${platforms.find((p: any) => p.reviews)?.reviews || reviews.length} Reviews`
                    )
                ])
            ]),
            React.createElement("div", { className: "p-5 grid grid-cols-1 gap-3 flex-1 overflow-y-auto pr-1" },
                reviews.map((rev: any, i: number) => {
                    const isActive = activeReview === i;
                    return React.createElement("div", {
                        key: i,
                        onClick: () => setActiveReview(isActive ? null : i),
                        className: `p-3 rounded-xl border flex gap-3 items-start transition-all cursor-pointer ${isActive ? "bg-white border-indigo-200 shadow-sm" : "bg-slate-50/70 border-slate-200 hover:bg-white"}`
                    }, [
                        React.createElement("div", { className: "w-10 h-10 rounded-full bg-white flex items-center justify-center text-[11px] font-black text-slate-600 shadow-sm border border-slate-200 flex-shrink-0" }, rev.avatar),
                        React.createElement("div", { className: "flex-1" }, [
                            React.createElement("div", { className: "flex justify-between items-start mb-1" }, [
                                React.createElement("div", {}, [
                                    React.createElement("p", { className: "text-sm font-black text-gray-900" }, rev.user),
                                    React.createElement("p", { className: "text-[10px] text-slate-400 font-bold uppercase tracking-wider" }, rev.platform)
                                ]),
                                React.createElement("div", { className: "text-amber-600 text-[10px] font-black" }, `${rev.rating}/5`)
                            ]),
                            React.createElement("p", { className: `text-xs text-slate-600 leading-relaxed ${isActive ? "line-clamp-none" : "line-clamp-2"}` }, `"${rev.text}"`),
                            React.createElement("div", { className: "flex items-center justify-between mt-2" }, [
                                React.createElement("p", { className: "text-[9px] text-slate-400 font-black uppercase" }, rev.date),
                                isActive && React.createElement("button", {
                                    onClick: (e: any) => { e.stopPropagation(); onNavigate('chat'); },
                                    className: "text-[9px] font-black uppercase tracking-widest text-indigo-600 hover:text-indigo-700"
                                }, "Open Thread")
                            ])
                        ])
                    ]);
                })
            ),
            React.createElement("div", { className: "p-4 bg-slate-50/70 border-t border-slate-100 flex justify-center" },
                React.createElement("button", { className: "text-xs font-black text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded-xl transition-all" }, "View All Reviews →")
            )
        ]),
    ]);
}
